import apiClient from './_axios';

/** One day's collections, for the Home chart. */
export interface IspCollectionDay {
   /** YYYY-MM-DD, PH calendar day. */
   date?: string;
   /** Pre-formatted for the axis, so the chart does no date work. */
   label: string;
   amount: number;
   /** Cash and "other", taken by hand. */
   cash?: number;
   /** GCash, Maya and bank. */
   online?: number;
}

export interface IspHomeSummary {
   activeSubscribers: number;
   /** Past due but still inside grace — chase these before they become cut-offs. */
   overdue: number;
   suspended: number;
   collectedThisMonth: number;
   /** Everything taken in the chosen window. */
   collectedInRange: number;
   /**
    * Every payment ever, summed on the server.
    *
    * 🔴 Not the windowed rows added up — an all-time figure from a 30-day
    * query is a 30-day figure wearing the wrong label, and it is the number an
    * operator judges the whole business by.
    */
   collectedAllTime: number;
   days: number;
   /** Range totals, split the way the chart is. */
   collectedCash?: number;
   collectedOnline?: number;
   collections: IspCollectionDay[];
}

/** The counts and the chart behind ISP Business → Dashboard. */
/** A from–to range (YYYY-MM-DD) like the Piso WiFi chart; empty = last 30 days. */
export async function getIspHome(
   range: { dateFrom?: string; dateTo?: string } = {}
): Promise<IspHomeSummary> {
   const r = await apiClient.get<IspHomeSummary>('/isp/home', {
      params:
         range.dateFrom && range.dateTo
            ? { dateFrom: range.dateFrom, dateTo: range.dateTo }
            : { days: 30 },
   });
   return r.data;
}

/** The operator's own ISP business. Null until they have set one up. */
export interface IspTenant {
   id: string;
   businessName: string;
   /** Subdomain slug for the subscriber portal, later. */
   portalSlug: string;
   logoUrl: string;
   /** Both appear on every invoice a subscriber is handed. */
   address: string;
   contactNumber: string;
   /**
    * Every way a subscriber can pay. A list, because operators really do take
    * GCash, Maya, a bank transfer and cash at once — and somebody staring at an
    * overdue bill should see all of them.
    */
   paymentMethods: IspPaymentMethod[];
   reconnectOnPartialPayment?: boolean;
   /** Email the customer their bill and remind them before it falls due. */
   remindersEnabled?: boolean;
   /** Days before the due date to warn them. 0 turns just that one off. */
   remindBeforeDays?: number;
}

export type IspPaymentKind = 'gcash' | 'maya' | 'bank' | 'cash' | 'other';

export interface IspPaymentMethod {
   id: string;
   kind: IspPaymentKind;
   /** Account name, or what to write on the envelope for cash. */
   accountName: string;
   accountNumber: string;
   /** Uploaded QR, for the wallets that have one. Never set for cash. */
   qrUrl: string;
   instructions: string;
   /**
    * 🔴 Off, not deleted. Old payments point at this id, so a method an
    * operator has stopped using still has to exist for last year's receipts to
    * make sense.
    */
   active: boolean;
}

/**
 * Replace the whole list in one call.
 *
 * Whole rather than per-row because the operator edits a short list on one
 * screen and presses save once. Anything left out comes back DEACTIVATED, not
 * deleted — the server will not destroy a method that payments point at.
 */
export type IspPaymentMethodInput = Omit<IspPaymentMethod, 'id'> & {
   /** Absent on a brand-new method; present keeps the id payments point at. */
   id?: string;
};

export async function saveIspPaymentMethods(
   paymentMethods: IspPaymentMethodInput[]
): Promise<IspTenant> {
   const r = await apiClient.put<{ tenant: IspTenant }>(
      '/isp/tenant/payment-methods',
      { paymentMethods }
   );
   return r.data.tenant;
}

export async function getIspTenant(): Promise<IspTenant | null> {
   const r = await apiClient.get<{ tenant: IspTenant | null }>('/isp/tenant');
   return r.data.tenant;
}

export interface IspTenantInput {
   businessName: string;
   remindersEnabled?: boolean;
   remindBeforeDays?: number;
   /** Shown to customers on invoices and the payment page. */
   logoUrl?: string;
   address?: string;
   contactNumber?: string;
}

/** Create or update. Deliberately an upsert — see the route comment. */
export async function saveIspTenant(input: IspTenantInput): Promise<IspTenant> {
   const r = await apiClient.post<{ tenant: IspTenant }>('/isp/tenant', input);
   return r.data.tenant;
}

/* ---------------------------------------------------------------- servers */

/**
 * One reading off the router.
 *
 * ⚠️ EVERY NUMBER IS NULLABLE AND NULL MEANS "NOT MEASURED", never zero. A
 * router SmartFi could not reach must not render as a box sitting at 0% CPU with
 * no subscribers on it — that reads as a healthy idle server, which is the
 * opposite of what happened.
 */
export interface IspServerHealth {
   cpuLoadPct: number | null;
   /** DERIVED. RouterOS reports FREE memory; the server converts it. */
   memoryUsedPct: number | null;
   memoryTotalBytes: number | null;
   storageUsedPct: number | null;
   storageTotalBytes: number | null;
   uptimeSec: number | null;
   /** What the box says it is — a mismatch with `model` is worth showing. */
   boardName: string;
   /** PPPoE sessions up right now. */
   onlineClients: number | null;
   /** PPPoE secrets on the ROUTER, including subscribers not yet in SmartFi. */
   routerSecretCount: number | null;
   lastProbeAt: string | null;
   /** Why the last read failed, in words the operator can act on. */
   lastProbeError: string;
}

/**
 * Subscriber counts from SmartFi's OWN records.
 *
 * ⚠️ NOT off the router, and never off it. `suspended` is a billing state the
 * MikroTik knows nothing about — it only sees a secret on a walled-garden
 * profile. These stay at zero until the Accounts page exists to create any.
 */
export interface IspServerAccounts {
   total: number;
   waiting: number;
   active: number;
   suspended: number;
   disconnected: number;
}

/**
 * How far through the two-part paste a MikroTik is.
 *
 * 🔴 `tunnel-only` is the state the split exists to expose: part 1 landed, part
 * 2 was truncated away by Winbox. The router has a live tunnel and answers
 * nothing — online-looking and unusable.
 */
export type IspSetupStage = 'waiting' | 'tunnel-only' | 'ready' | 'offline';

export interface IspServerRow {
   /** Subscriber VLAN SmartFi built (0 = none) and where. */
   subscriberVlan?: number;
   subscriberPort?: string;
   /** SmartFi built the PPPoE server, so Edit may move its VLAN. */
   smartfiBuilt?: boolean;
   /**
    * Set when the router serves no PPPoE at all — plans and customers can be
    * set up and nobody can connect. Empty when there is nothing to say.
    */
   pppoeWarning?: string;
   /** Why SmartFi built nothing on this router. Empty when it built fine. */
   setupBlockedBy?: string;
   /** The short name a human reads out loud — MTK-1A2B3C4D. */
   deviceCode?: string;
   id: string;
   name: string;
   model: string;
   vpnIp: string;
   routerOsVersion: string;
   usesRadius: boolean;
   servers: { name: string; serviceName: string; interfaceName: string }[];
   /** Has the operator ever pasted the script? */
   everConnected: boolean;
   /** A recent tunnel handshake — the router is really there right now. */
   online: boolean;
   handshakeAgoSec: number | null;
   setupStage: IspSetupStage;
   /** The router has fetched part 2 — so "tunnel-only" is a probe not yet re-run, not a missing paste. */
   part2Fetched?: boolean;
   health: IspServerHealth;
   accounts: IspServerAccounts;
}

export async function getIspServers(): Promise<IspServerRow[]> {
   const r = await apiClient.get<{ servers: IspServerRow[] }>('/isp/servers');
   return r.data.servers;
}

export interface IspServerModel {
   id: string;
   label: string;
   wanPort: string;
   /** Where an OLT is usually plugged in. '' = the board has no SFP. */
   sfpPort?: string;
}

export async function getIspServerModels(): Promise<IspServerModel[]> {
   const r = await apiClient.get<{ models: IspServerModel[] }>(
      '/isp/server-models'
   );
   return r.data.models;
}

/**
 * One block to paste into Winbox, on its own.
 *
 * 🔴 TWO OF THESE, NEVER ONE. Ray, 2026-08-29: *"that long script will
 * truncated."* Winbox's terminal drops the tail of a long paste with no error at
 * all — the prompt returns, the operator believes it worked, and the MikroTik is
 * half configured. The Add-Vendo MikroTik flow splits its paste into 4a and 4b
 * for exactly this reason.
 */
export interface IspScriptPart {
   id: 'tunnel' | 'access' | 'suspended' | 'redirect';
   label: string;
   /** What this block does, in the operator's words. */
   what: string;
   /** What they should see on screen before moving on. */
   then: string;
   script: string;
   /**
    * The full text, when `script` is the two-line fetch that replaces it.
    *
    * A router behind a proxy or a blocked DNS cannot fetch, and an operator
    * staring at a fetch error needs somewhere to go that is not "call
    * support". Absent when there is nothing to fall back to.
    */
   fallback?: string;
}

export async function createIspServer(input: {
   name: string;
   model: string;
   /** '' = every LAN port; else the model's SFP port name. */
   subscriberPort?: string;
   /** 0 = untagged; else the VLAN subscribers arrive tagged on. */
   subscriberVlan?: number;
}): Promise<{ id: string; name: string; vpnIp: string; parts: IspScriptPart[] }> {
   const r = await apiClient.post('/isp/servers', input);
   return r.data as {
      id: string;
      name: string;
      vpnIp: string;
      parts: IspScriptPart[];
   };
}

export async function getIspServerScript(
   id: string
): Promise<IspScriptPart[]> {
   const r = await apiClient.get<{ parts: IspScriptPart[] }>(
      `/isp/servers/${id}/script`
   );
   return r.data.parts;
}

/**
 * The setup scripts and the undo, in one call.
 *
 * The delete dialog needs the cleanup script the moment it opens — what SmartFi
 * leaves behind on a live PPPoE router is the thing the operator has to be told
 * BEFORE they confirm, not after.
 */
export async function getIspServerScripts(
   id: string
): Promise<{ parts: IspScriptPart[]; cleanup: string }> {
   const r = await apiClient.get<{ parts: IspScriptPart[]; cleanup: string }>(
      `/isp/servers/${id}/script`
   );
   return r.data;
}

/**
 * Take a MikroTik off SmartFi.
 *
 * Rejects with a 409 while subscriber lines still point at it — an account
 * whose server does not exist still bills and has nowhere to be suspended.
 */
/** Edit a MikroTik: rename it, or change the VLAN subscribers arrive on. */
export async function updateIspServer(
   id: string,
   input: { name?: string; subscriberVlan?: number }
): Promise<{ ok: boolean; did: string[] }> {
   const r = await apiClient.put(`/isp/servers/${id}`, input);
   return r.data as { ok: boolean; did: string[] };
}

export async function deleteIspServer(id: string): Promise<void> {
   await apiClient.delete(`/isp/servers/${id}`);
}

/**
 * Read one router NOW rather than waiting for the background refresh.
 *
 * The list refreshes itself every ten seconds and probes what has gone stale,
 * which is right for a page left open and wrong for an operator who has just
 * pasted the script into Winbox and wants to know whether it took.
 *
 * Resolves with `ok:false` and a reason rather than throwing — a router refusing
 * our login is a finding, not a failure of the request.
 */
export async function probeIspServer(
   id: string
): Promise<{ ok: boolean; error: string }> {
   const r = await apiClient.post<{ ok: boolean; error: string }>(
      `/isp/servers/${id}/probe`
   );
   return r.data;
}

/* ------------------------------------------------------------------ plans */

export interface IspPlanRow {
   id: string;
   /**
    * Every MikroTik this plan runs on — **may be empty**.
    *
    * 🔴 MANY, NOT ONE (Ray, 2026-08-31). An operator selling "Home 50" across
    * three barangays runs it on three routers, and one-plan-per-router would
    * make them keep three identical price rows in step by hand — which is how
    * two of them drift and a customer is billed for a speed they are not
    * getting.
    *
    * 🔴 AND EMPTY IS VALID. Price lists get written before any router is
    * registered at least as often as the other way round; the link is made when
    * one appears.
    */
   servers: string[];
   name: string;
   price: number;
   speedLabel: string;
   category: string;
   remarks: string;
   /** The /ppp/profile name on that router — what actually enforces the plan. */
   routerProfile: string;
   bandwidth: string;
   burstMax: string;
   burstThreshold: string;
   burstSeconds: number;
   localAddress: string;
   remoteAddress: string;
   /**
    * Days past due before an account on this plan is cut off.
    *
    * 🔴 PER PLAN, NOT PER BUSINESS (Ray, 2026-08-28) — a business package and a
    * ₱799 home line do not get the same patience.
    */
   graceDays: number;
   /**
    * When the speed limit last reached this plan's routers, and why it did not.
    *
    * 🔴 LINKED IS NOT ENFORCED. A plan can name a MikroTik that has never heard
    * of its profile, and every subscriber on it would quietly get the router's
    * default speed. Three states, not two.
    */
   profileSyncedAt: string | null;
   profileSyncError: string;
}

/**
 * What the operator can set. The sync fields are SERVER-OWNED — they record what
 * reached the hardware, which is not something a form gets to assert.
 */
export type IspPlanDraft = Omit<
   IspPlanRow,
   'id' | 'profileSyncedAt' | 'profileSyncError'
>;

export async function getIspPlans(): Promise<IspPlanRow[]> {
   const r = await apiClient.get<{ plans: IspPlanRow[] }>('/isp/plans');
   return r.data.plans;
}

/**
 * Saving a plan also writes its speed onto every router it names.
 *
 * `sync` says whether that reached the hardware. The save itself never fails
 * because a router is down — the price change is the operator's and belongs in
 * the database whatever the MikroTik is doing.
 */
export interface IspPlanSaved {
   plan: IspPlanRow;
   sync: { ok: boolean; message: string };
}

export async function createIspPlan(
   draft: IspPlanDraft
): Promise<IspPlanSaved> {
   const r = await apiClient.post<IspPlanSaved>('/isp/plans', draft);
   return r.data;
}

export async function updateIspPlan(
   id: string,
   draft: IspPlanDraft
): Promise<IspPlanSaved> {
   const r = await apiClient.put<IspPlanSaved>(`/isp/plans/${id}`, draft);
   return r.data;
}

export async function deleteIspPlan(id: string): Promise<void> {
   await apiClient.delete(`/isp/plans/${id}`);
}

/** One /ppp/profile as it really exists on a router. */
export interface IspRouterProfile {
   id: string;
   name: string;
   rateLimit: string;
   localAddress: string;
   remoteAddress: string;
}

/**
 * The profiles on a router, read now.
 *
 * Resolves with an empty list and a `reason` rather than throwing when the
 * router will not answer — the plan form has to stay usable and say why the
 * dropdown is empty, not collapse.
 */
export async function getIspRouterProfiles(
   serverId: string
): Promise<{ profiles: IspRouterProfile[]; reason: string }> {
   const r = await apiClient.get<{
      profiles: IspRouterProfile[];
      error: string;
   }>(`/isp/servers/${serverId}/profiles`);
   return { profiles: r.data.profiles, reason: r.data.error };
}

/** Create (or update) this plan's /ppp/profile on its router. */
export async function pushIspPlanProfile(id: string): Promise<{
   ok: boolean;
   /** One row per router, so one unreachable box cannot hide the three that took it. */
   results: { server: string; ok: boolean; created: boolean; error: string }[];
   message: string;
}> {
   const r = await apiClient.post(`/isp/plans/${id}/push-profile`);
   return r.data as {
      ok: boolean;
      results: { server: string; ok: boolean; created: boolean; error: string }[];
      message: string;
   };
}

/**
 * The IP pools and gateway addresses a router already uses.
 *
 * Exists so the plan form can suggest them instead of asking the operator a
 * RouterOS question they have no way to answer.
 */
export interface IspAddressing {
   pools: string[];
   localAddresses: string[];
   suggestedPool: string;
   suggestedLocal: string;
   error: string;
}

export async function getIspRouterAddressing(
   serverId: string
): Promise<IspAddressing> {
   const r = await apiClient.get<IspAddressing>(
      `/isp/servers/${serverId}/addressing`
   );
   return r.data;
}

/**
 * What the operator has set up so far.
 *
 * Drives the sidebar's greyed-out items. ⚠️ Only STRUCTURAL dependencies live
 * here — plans and servers can be made in either order and neither gates the
 * other (Ray, 2026-08-31).
 */
export interface IspReadiness {
   hasTenant: boolean;
   customers: number;
   plans: number;
   servers: number;
   accounts: number;
}

export async function getIspReadiness(): Promise<IspReadiness> {
   const r = await apiClient.get<IspReadiness>('/isp/readiness');
   return r.data;
}

/** Plans written before any MikroTik existed, still waiting for one. */
export async function getUnlinkedIspPlans(): Promise<IspPlanRow[]> {
   const r = await apiClient.get<{ plans: IspPlanRow[] }>('/isp/plans/unlinked');
   return r.data.plans;
}

/**
 * Attach plans that have no server to this one.
 *
 * The moment a MikroTik is registered is the moment "which router enforces this
 * plan" becomes answerable, so the link is offered there rather than sending the
 * operator back through Settings to edit each plan by hand.
 */
export async function adoptIspPlans(
   serverId: string,
   planIds: string[]
): Promise<{ linked: number }> {
   const r = await apiClient.post<{ linked: number }>(
      `/isp/servers/${serverId}/adopt-plans`,
      { planIds }
   );
   return r.data;
}

/* -------------------------------------------------------------- customers */

export interface IspCustomerRow {
   id: string;
   /** GENERATED, never typed — two customers with one code posts a payment to the wrong person. */
   customerCode: string;
   fullName: string;
   phone: string;
   email: string;
   address: string;
   idType: string;
   idPhotoUrl: string;
   /** Plan id they asked for on the public form. Empty if typed in by hand. */
   wantedPlan: string;
   /** When they applied. Used for the "waiting N days" countdown. */
   appliedAt: string | null;
   status: 'pending' | 'approved' | 'rejected';
   notes: string;
   /** How many subscriber lines they hold. */
   accounts: number;
}

export type IspCustomerDraft = Omit<
   IspCustomerRow,
   'id' | 'customerCode' | 'accounts'
>;

export async function getIspCustomers(): Promise<IspCustomerRow[]> {
   const r = await apiClient.get<{ customers: IspCustomerRow[] }>(
      '/isp/customers'
   );
   return r.data.customers;
}

export async function createIspCustomer(
   draft: IspCustomerDraft
): Promise<IspCustomerRow> {
   const r = await apiClient.post<{ customer: IspCustomerRow }>(
      '/isp/customers',
      draft
   );
   return r.data.customer;
}

export async function updateIspCustomer(
   id: string,
   draft: IspCustomerDraft
): Promise<IspCustomerRow> {
   const r = await apiClient.put<{ customer: IspCustomerRow }>(
      `/isp/customers/${id}`,
      draft
   );
   return r.data.customer;
}

/**
 * Approve or reject an application.
 *
 * Its own call rather than a field on the customer form: approving is a
 * DECISION, editing is a correction, and folding them together lets an operator
 * fixing a misspelt street approve a stranger by accident.
 *
 * Approving connects nobody — it only makes them eligible for a connection the
 * operator still has to set up.
 */
export async function decideIspCustomer(
   id: string,
   decision: 'approved' | 'rejected',
   reason?: string
): Promise<IspCustomerRow> {
   const r = await apiClient.put<{ customer: IspCustomerRow }>(
      `/isp/customers/${id}/decision`,
      { decision, reason }
   );
   return r.data.customer;
}

/** Rejects with a 409 while they still hold a connection. */
export async function deleteIspCustomer(id: string): Promise<void> {
   await apiClient.delete(`/isp/customers/${id}`);
}

/** Create the three starter plans for a business that has none. */
export async function createStarterPlans(): Promise<IspPlanRow[]> {
   const r = await apiClient.post<{ plans: IspPlanRow[] }>(
      '/isp/plans/starters'
   );
   return r.data.plans;
}

/* --------------------------------------------------------------- accounts */

export interface IspAccountRow {
   /** One of the operator's three lifetime free subscribers. */
   isFree?: boolean;
   /** Days until their soonest open bill. Negative = past due. Null = none. */
   daysLeft?: number | null;
   /**
    * Is that countdown about THIS line, or another of the same customer's?
    *
    * A person's debt cuts off every line they have, so it appears on all their
    * rows — but "3d overdue" on a line that has never been billed reads as an
    * accusation against that line.
    */
   daysLeftIsOwn?: boolean;
   /** Days until auto-suspend cuts this line off, counting the plan's grace. */
   cutoffInDays?: number | null;
   /** The date the next bill actually lands — the server's answer, not a guess. */
   nextBillAt?: string | null;
   /** The plan's grace in days, so the column can say what it counts. */
   graceDays?: number;
   /** Down + up so far today, Manila. */
   usageTodayBytes?: number;
   /** Down + up since the line was installed. */
   usageLifetimeBytes?: number;
   /** The short name a human reads out loud — ACC-1A2B3C4D. */
   accountCode?: string;
   /** When the current PPPoE session began. Null when they are not connected. */
   onlineSince?: string | null;
   /** Last time SmartFi saw this line up at all. */
   lastOnlineAt?: string | null;
   /**
    * Down + up this month, in bytes.
    *
    * Accumulated from samples, because RouterOS's own counter resets on every
    * reconnect — read once it would report "since their last reboot".
    */
   usageBytes?: number;
   id: string;
   customer: string;
   server: string;
   plan: string;
   serverName: string;
   /** Day of the month this LINE falls due — per account, not per customer. */
   dueDay: number;
   pppoeUsername: string;
   status: 'waiting' | 'active' | 'suspended' | 'disconnected';
   installedAt: string | null;
   lastSyncedAt: string | null;
   /** Why the router did not take this login. */
   lastError: string;
   /**
    * What this CUSTOMER still owes, across every open invoice.
    *
    * 🔴 PER CUSTOMER, NOT PER ACCOUNT. An invoice can cover two of somebody's
    * lines, so splitting the debt between them would invent a number that
    * appears on no bill.
    */
   balance: number;
   /** At least one of their open bills is past its due date. */
   overdue: boolean;
}

export interface IspAccountDraft {
   customer: string;
   server: string;
   plan: string;
   serverName: string;
   dueDay: number;
   pppoeUsername: string;
   /** Sent as typed — the customer's ONU authenticates with the real characters. */
   pppoePassword: string;
}

/**
 * Change a live line's plan, login or bill day.
 *
 * 🔴 The server writes the ROUTER first and our record only if that succeeded,
 * so a 502 here means nothing changed anywhere. Ray, 2026-09-01: *"in accounts
 * it should have upgrade plan or edit specially the login."*
 */
/**
 * One line's PPPoE login, for reading back to a customer who lost theirs.
 *
 * 🔴 Deliberately NOT in the accounts list: a password in every row of a page
 * that gets screenshotted and left open on a counter is a leak with no purpose.
 * Fetched one at a time, when an operator opens that customer's connection.
 */
export async function getIspAccountCredentials(
   id: string
): Promise<{ pppoeUsername: string; pppoePassword: string }> {
   const r = await apiClient.get(`/isp/accounts/${id}/credentials`);
   return r.data as never;
}

export async function updateIspAccount(
   id: string,
   input: { plan?: string; pppoePassword?: string; dueDay?: number }
): Promise<{ account: IspAccountRow; message: string }> {
   const r = await apiClient.put(`/isp/accounts/${id}`, input);
   return r.data as never;
}

export interface IspAccountsPage {
   accounts: IspAccountRow[];
   /** How many of the three lifetime free subscribers are spent. */
   freeUsed: number;
   freeTotal: number;
}

export async function getIspAccounts(
   customer?: string
): Promise<IspAccountsPage> {
   const r = await apiClient.get<IspAccountsPage>('/isp/accounts', {
      params: customer ? { customer } : undefined,
   });
   return {
      accounts: r.data.accounts ?? [],
      freeUsed: Number(r.data.freeUsed ?? 0),
      freeTotal: Number(r.data.freeTotal ?? 3),
   };
}

/**
 * Creating a line also writes its PPPoE login onto the router.
 *
 * `sync` says whether that reached the hardware. The account saves either way —
 * losing the operator's typing to a router that is down would be worse than an
 * account they can retry — but it stays `waiting`, never `active`.
 */
export async function createIspAccount(
   draft: IspAccountDraft
): Promise<{ account: IspAccountRow; sync: { ok: boolean; message: string } }> {
   const r = await apiClient.post('/isp/accounts', draft);
   return r.data as {
      account: IspAccountRow;
      sync: { ok: boolean; message: string };
   };
}

/** Swaps the secret onto the walled-garden profile and drops the live session. */
/** `reason` is shown to the CUSTOMER on their bill page. */
export async function suspendIspAccount(
   id: string,
   reason?: string
): Promise<IspAccountRow & { warning?: string }> {
   const r = await apiClient.post<{ account: IspAccountRow; warning?: string }>(
      `/isp/accounts/${id}/suspend`,
      { reason }
   );
   // 🔴 The warning must survive to the caller. It says the router never heard
   // us and the change is queued — the difference between a line that is cut
   // and one that is about to be.
   return { ...r.data.account, warning: r.data.warning };
}

/**
 * The customer has left: the login comes off the router, the history stays.
 *
 * `warning` is set when the billing state changed but the router could not be
 * reached — the operator must be able to stop invoicing somebody while their
 * MikroTik is down.
 */
export async function disconnectIspAccount(
   id: string
): Promise<{ account: IspAccountRow; warning: string }> {
   const r = await apiClient.post(`/isp/accounts/${id}/disconnect`);
   return r.data as { account: IspAccountRow; warning: string };
}

/**
 * Remove a line for good. Refuses unless it is already disconnected.
 *
 * 🔴 This did not exist until 2026-09-09, and its absence trapped an operator:
 * a customer could not be deleted while any account existed, a server could not
 * be removed while any account pointed at it, and an account could never be
 * removed at all.
 */
export async function deleteIspAccount(
   id: string
): Promise<{ ok: true; warning: string }> {
   const r = await apiClient.delete(`/isp/accounts/${id}`);
   return r.data as { ok: true; warning: string };
}

export async function resumeIspAccount(
   id: string
): Promise<IspAccountRow & { warning?: string }> {
   const r = await apiClient.post<{ account: IspAccountRow; warning?: string }>(
      `/isp/accounts/${id}/resume`
   );
   return { ...r.data.account, warning: r.data.warning };
}

/* --------------------------------------------------------------- invoices */

export interface IspInvoiceRow {
   id: string;
   customer: string;
   number: string;
   periodStart: string;
   periodEnd: string;
   dueAt: string;
   /** When the bill was raised — not the same as when it is due. */
   createdAt: string | null;
   lines: { account: string; planName: string; amount: number }[];
   total: number;
   paidAmount: number;
   outstanding: number;
   status: 'unpaid' | 'partial' | 'paid' | 'void';
   paidAt: string | null;
   /**
    * A payment the customer says they made, waiting to be checked.
    *
    * Lives on the invoice rather than on a screen of its own (Ray,
    * 2026-09-01): a claim is about ONE bill, and a separate queue is a second
    * place to look for something that belongs beside the thing it is about.
    */
   pendingProof: {
      id: string;
      amount: number;
      reference: string;
      proofUrl: string;
      note: string;
      createdAt: string | null;
   } | null;
}

/**
 * Take a settled bill off the working list — or put it back.
 *
 * 🔴 It is never deleted. An invoice is what a payment points at and what an
 * argument about money is settled with. The server refuses to archive one that
 * still owes money: hiding a debt is how it gets forgotten.
 */
export async function archiveIspInvoice(
   id: string,
   archived = true
): Promise<{ ok: boolean; message: string }> {
   const r = await apiClient.put(`/isp/invoices/${id}/archive`, { archived });
   return r.data as never;
}

export async function getIspInvoices(params?: {
   customer?: string;
   status?: string;
   archived?: boolean;
}): Promise<IspInvoiceRow[]> {
   const r = await apiClient.get<{ invoices: IspInvoiceRow[] }>('/isp/invoices', {
      params,
   });
   return r.data.invoices;
}

/**
 * Bill everyone who has reached their due day.
 *
 * Safe to press twice: it checks for an invoice already covering the period, so
 * a customer never ends up holding two bills for one month.
 */
export async function generateIspInvoices(): Promise<{
   created: IspInvoiceRow[];
   skipped: number;
   message: string;
}> {
   const r = await apiClient.post('/isp/invoices/generate');
   return r.data as { created: IspInvoiceRow[]; skipped: number; message: string };
}

export interface IspPaymentDraft {
   customer: string;
   amount: number;
   method: 'cash' | 'gcash' | 'bank' | 'other';
   reference?: string;
   notes?: string;
}

/**
 * Record money received. It clears the OLDEST debt first — that is the invoice
 * about to get them cut off — and anything above what they owe comes back as
 * `unapplied` rather than disappearing into a total nobody can explain.
 */
export async function createIspPayment(draft: IspPaymentDraft): Promise<{
   applied: { id: string; amount: number }[];
   unapplied: number;
   message: string;
}> {
   const r = await apiClient.post('/isp/payments', draft);
   return r.data as {
      applied: { id: string; amount: number }[];
      unapplied: number;
      message: string;
   };
}

/* --------------------------------------- what the operator owes SmartFi */

export interface IspSmartfiBilling {
   /** FREE or PAID — which card on the Subscriptions page is theirs. */
   subscription: 'free' | 'paid';
   /** BILLABLE active subscribers — the free three are not counted. */
   activeAccounts: number;
   /** Free subscribers live right now. */
   freeActive: number;
   /** How many of the three lifetime free slots remain. */
   freeSlotsLeft: number;
   tier: { upTo: number; pricePerAccount: number } | null;
   /** PESOS per active subscriber per month — the bands are a price list. */
   pricePerAccount: number;
   /** What the month costs in money. */
   monthlyPeso: number;
   /**
    * The same bill at the LIVE rate, which is what actually gets debited.
    *
    * 🔴 NOT ALWAYS EQUAL TO THE PESO FIGURE. `pointsPerCredit` is admin-editable,
    * so a point is worth ₱1 only while it happens to equal `baseCreditPrice`.
    * Sent converted so no screen ever does that arithmetic itself.
    */
   monthlyPoints: number;
   pointsPerPeso: number;
   /**
    * The ISP conversion rate — NOT the loyalty `pointsPerCredit`. Separate on
    * purpose so ISP margin can be tuned without repricing every reward.
    */
   ispPointsPerCredit: number;
   baseCreditPrice: number;
   pointsBalance: number;
   /** Credits on hand — what they convert FROM, shown so the advice is actionable. */
   creditBalance: number;
   /** What converting all of them would yield at the current ISP rate. */
   creditsWorthPoints: number;
   /**
    * Months already billed, newest first — frozen at the prices in force when
    * each was raised, so a later price change never restates an old month.
    */
   charges: {
      period: string;
      accounts: number;
      pricePerAccount: number;
      totalPeso: number;
      totalPoints: number;
      status: 'unpaid' | 'paid' | 'waived';
      paidAt: string | null;
   }[];
   /** Arrears across every unpaid month. NOT the same as this month's bill. */
   owedPoints: number;
   shortfall: number;
   /**
    * False while they are short. The ONLY consequence: they cannot add
    * subscribers. Nobody already connected is ever cut off over an operator's
    * top-up — those are the people who did nothing wrong.
    */
   canAddAccounts: boolean;
   tiers: { upTo: number; pricePerAccount: number }[];
}

/** The button on each Subscriptions card. */
export async function setIspSubscription(
   subscription: 'free' | 'paid'
): Promise<{ subscription: 'free' | 'paid' }> {
   const r = await apiClient.put('/isp/subscription', { subscription });
   return r.data as { subscription: 'free' | 'paid' };
}

export async function getIspSmartfiBilling(): Promise<IspSmartfiBilling> {
   const r = await apiClient.get<IspSmartfiBilling>('/isp/smartfi-billing');
   return r.data;
}

export interface IspCreditConversion {
   creditsSpent: number;
   pointsGranted: number;
   pointsBalance: number;
   rate: number;
}

/**
 * Turn credits into points for ISP billing, at `ispPointsPerCredit`.
 *
 * Offered on the Billing screen itself: a shortfall warning an operator has to
 * leave the page to act on is a dead end (Ray, 2026-09-01).
 */
export async function convertCreditsForIsp(
   credits: number
): Promise<IspCreditConversion> {
   const r = await apiClient.post<IspCreditConversion>(
      '/isp/smartfi-billing/convert',
      { credits }
   );
   return r.data;
}

/* ------------------------------------------------- public application page */

export interface IspPublicTenant {
   businessName: string;
   logoUrl: string;
   address: string;
   contactNumber: string;
   plans: {
      id: string;
      name: string;
      price: number;
      speedLabel: string;
   }[];
}

/**
 * The operator's shopfront, for anybody at all — no login.
 *
 * 🔴 Deliberately thin. It carries what an operator would print on a leaflet
 * and nothing else: no payment methods, no subscriber counts, no routers. A
 * payment QR on an unauthenticated page is an invitation to clone the shopfront
 * and collect somebody else's money.
 */
export async function getIspPublicTenant(
   slug: string
): Promise<IspPublicTenant> {
   const r = await apiClient.get<IspPublicTenant>(
      `/isp/public/${encodeURIComponent(slug)}`
   );
   return r.data;
}

export interface IspApplication {
   fullName: string;
   phone: string;
   address: string;
   email?: string;
   notes?: string;
   /** The plan they picked. A WISH — the operator still chooses the real one. */
   plan?: string;
   idType?: string;
   idPhotoUrl?: string;
}

/** Ask to be connected. Creates a PENDING customer the operator must approve. */
export async function applyToIspTenant(
   slug: string,
   input: IspApplication
): Promise<{ ok: boolean; duplicate: boolean; message: string }> {
   const r = await apiClient.post<{
      ok: boolean;
      duplicate: boolean;
      message: string;
   }>(`/isp/public/${encodeURIComponent(slug)}/apply`, input);
   return r.data;
}

/* -------------------------------------------------- the subscriber portal */

export interface IspPortalMe {
   businessName: string;
   logoUrl: string;
   contactNumber: string;
   address: string;
   customer: { fullName: string; customerCode: string };
   accounts: {
      id: string;
      name: string;
      status: string;
      dueDay: number;
      /** What they are paying for — a bill that never names the plan is a puzzle. */
      planName: string;
      speedLabel: string;
      planPrice: number;
      /** Why the operator cut them off BY HAND, in their own words. */
      suspendReason: string;
      onlineSince: string | null;
      lastOnlineAt: string | null;
   }[];
   /** Separate from invoices: "what did I pay" is not "what was I billed". */
   payments: {
      id: string;
      amount: number;
      method: string;
      reference: string;
      receivedAt: string;
   }[];
   outstanding: number;
   invoices: {
      id: string;
      number: string;
      periodStart: string;
      periodEnd: string;
      dueAt: string;
      total: number;
      paidAmount: number;
      outstanding: number;
      status: string;
      paidAt: string | null;
      /** So the customer can open and PRINT the bill, not just a summary row. */
      lines: { planName: string; amount: number }[];
   }[];
   paymentMethods: {
      id: string;
      kind: string;
      accountName: string;
      accountNumber: string;
      qrUrl: string;
      instructions: string;
   }[];
   proofs: {
      id: string;
      amount: number;
      status: 'pending' | 'approved' | 'rejected';
      decisionReason: string;
      createdAt: string | null;
      /** When the operator decided. Null while it is still waiting. */
      decidedAt?: string | null;
   }[];
}

/**
 * Sign a subscriber in with their mobile number.
 *
 * 409 with `needsSurname` means the number belongs to more than one person —
 * families share numbers, and showing the first match would hand somebody a
 * stranger's bills.
 */
export async function startIspPortalSession(
   slug: string,
   phone: string,
   surname?: string
): Promise<{
   token: string;
   customer: { fullName: string; customerCode: string };
   businessName: string;
}> {
   const r = await apiClient.post(
      `/isp/portal/${encodeURIComponent(slug)}/session`,
      { phone, surname }
   );
   return r.data as never;
}

/** The portal token is NOT a login — it opens one customer's bills, for 12h. */
export async function getIspPortalMe(token: string): Promise<IspPortalMe> {
   const r = await apiClient.get<IspPortalMe>('/isp/portal/me', {
      headers: { Authorization: `Bearer ${token}` },
   });
   return r.data;
}

/** 🔴 Files a CLAIM. Nothing is credited until the operator approves it. */
export async function submitIspPortalProof(
   token: string,
   input: {
      amount: number;
      proofUrl: string;
      methodId?: string;
      reference?: string;
      note?: string;
   }
): Promise<{ id: string; message: string }> {
   const r = await apiClient.post('/isp/portal/proof', input, {
      headers: { Authorization: `Bearer ${token}` },
   });
   return r.data as never;
}

/* ------------------------------- the operator's queue of payment claims */

export interface IspPaymentProofRow {
   id: string;
   customer: string;
   customerName: string;
   customerCode: string;
   amount: number;
   reference: string;
   proofUrl: string;
   note: string;
   status: 'pending' | 'approved' | 'rejected';
   decisionReason: string;
   createdAt: string | null;
}

export async function getIspPaymentProofs(): Promise<IspPaymentProofRow[]> {
   const r = await apiClient.get<{ proofs: IspPaymentProofRow[] }>(
      '/isp/payment-proofs'
   );
   return r.data.proofs;
}

/**
 * Approve or reject a claim.
 *
 * `amount` is what ACTUALLY arrived, which the operator may correct — a
 * screenshot showing ₱500 against a ₱999 bill is a partial payment, not a wrong
 * one (Ray, 2026-09-01).
 */
export async function decideIspPaymentProof(
   id: string,
   decision: 'approved' | 'rejected',
   opts: { amount?: number; reason?: string } = {}
): Promise<{ ok: boolean; message?: string }> {
   const r = await apiClient.put(`/isp/payment-proofs/${id}/decision`, {
      decision,
      ...opts,
   });
   return r.data as never;
}

/**
 * Did the walled-garden pastes actually land on the router?
 *
 * Read from the MikroTik, never inferred from a button press: Winbox truncates
 * a paste SILENTLY, so each half is checked separately and a half-arrived
 * script has a name.
 */
export async function getIspWalledGardenState(
   serverId: string
): Promise<{ profile: boolean; wall: boolean; redirect: boolean }> {
   const r = await apiClient.get(`/isp/servers/${serverId}/walled`);
   return r.data as never;
}

/**
 * Switch the walled garden on, from SmartFi.
 *
 * Ray, 2026-09-02: *"why not do it."* By the time this can run, SmartFi
 * already has a REST account on the router and writes profiles and secrets
 * with it — a script for the operator to copy was ceremony, and every paste is
 * another chance for Winbox to truncate something silently.
 *
 * Safe to press twice: each step reads before it writes, and the reply says
 * what actually changed rather than "done".
 */
export async function applyIspWalledGarden(
   serverId: string
): Promise<{ did: string[]; already: string[]; message: string }> {
   const r = await apiClient.post(`/isp/servers/${serverId}/walled`);
   return r.data as never;
}

/**
 * Upload a proof screenshot from the customer portal.
 *
 * 🔴 NOT the dashboard's uploader. That posts to a route behind `mustAuth`, so
 * a subscriber — who has no login and never will — got a 401 and a button that
 * did nothing and said nothing. This one is guarded by their portal token.
 */
export async function uploadIspPortalProof(
   token: string,
   file: File,
   onProgress?: (percent: number) => void
): Promise<{ imageUrl: string }> {
   const form = new FormData();
   form.append('image', file);
   const r = await apiClient.post('/isp/portal/upload', form, {
      headers: {
         Authorization: `Bearer ${token}`,
         /*
          * 🔴 REQUIRED. apiClient defaults every request to application/json,
          * and that default wins over the FormData body — so the photo went up
          * as JSON, multer found no file, and the customer was told "No image
          * received." Every other working uploader in this codebase sets this
          * line; mine did not.
          */
         'Content-Type': 'multipart/form-data',
      },
      // A receipt over a phone connection that has just been cut off.
      timeout: 300000,
      onUploadProgress: (e) => {
         if (e.total && onProgress) {
            onProgress(Math.round((e.loaded * 100) / e.total));
         }
      },
   });
   return r.data as never;
}

/**
 * A short-lived URL for one bill, as a real PDF file.
 *
 * 🔴 The page must NAVIGATE to what this returns, never try to build or
 * download a file itself — an in-app browser blocks that silently, which is
 * how "Save PDF" managed to do nothing three times running.
 */
export async function getIspInvoicePdfLink(
   token: string,
   invoiceId: string
): Promise<string> {
   const r = await apiClient.post(
      `/isp/portal/invoice/${encodeURIComponent(invoiceId)}/pdf-link`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
   );
   return (r.data as { url: string }).url;
}

/* ------------------------------------- every operator, for SmartFi staff */

export interface IspAdminOverview {
   tenants: number;
   servers: number;
   serversOnline: number;
   accounts: number;
   accountsActive: number;
   accountsSuspended: number;
   customers: number;
   pointsThisMonth: number;
   pointsUnpaid: number;
   /** Everything ever charged, in points AND pesos. The peso figure is frozen
    *  on each charge, never points reconverted at today's rate. */
   pointsAllTime: number;
   pesoAllTime: number;
   pesoThisMonth: number;
   subscriberOutstanding: number;
   rows: {
      id: string;
      businessName: string;
      billable: number;
      freeUsed: number;
      suspended: number;
      servers: number;
      serversOnline: number;
      owedPoints: number;
      pointsBalance: number;
      creditBalance: number;
      salesPeso: number;
      joinedAt: string | null;
      lastSeenAt: string | null;
   }[];
}

/**
 * 🔴 COUNTS AND MONEY, NEVER PEOPLE. The server sends no router credentials,
 * no tunnel keys and no subscriber details — an operator's customers are not
 * SmartFi's to browse — and it has tests that fail if that ever changes.
 */
export async function getIspAdminOverview(): Promise<IspAdminOverview> {
   const r = await apiClient.get<IspAdminOverview>('/isp/admin/overview');
   return r.data;
}

export interface IspAdminDeviceRow {
   id: string;
   deviceCode: string;
   name: string;
   model: string;
   vpnIp: string;
   routerOsVersion: string;
   online: boolean;
   lastSeenAt: string | null;
   tenantName: string;
   ownerEmail: string;
   accounts: number;
   accountsActive: number;
   health: {
      cpuLoadPct: number | null;
      memoryUsedPct: number | null;
      storageUsedPct: number | null;
      uptimeSec: number | null;
      boardName: string;
   };
}

export interface IspAdminAccountRow {
   id: string;
   accountCode: string;
   pppoeUsername: string;
   status: string;
   isFree: boolean;
   tenantName: string;
   serverName: string;
   usageLifetimeBytes: number;
}

export type IspAdminTenantRow = IspAdminOverview['rows'][number] & {
   ownerEmail: string;
};

export async function getIspAdminDevices(): Promise<IspAdminDeviceRow[]> {
   const r = await apiClient.get<{ devices: IspAdminDeviceRow[] }>(
      '/isp/admin/devices'
   );
   return r.data.devices ?? [];
}

export async function getIspAdminAccountsAll(): Promise<IspAdminAccountRow[]> {
   const r = await apiClient.get<{ accounts: IspAdminAccountRow[] }>(
      '/isp/admin/accounts'
   );
   return r.data.accounts ?? [];
}

export async function getIspAdminTenants(): Promise<IspAdminTenantRow[]> {
   const r = await apiClient.get<{ tenants: IspAdminTenantRow[] }>(
      '/isp/admin/tenants'
   );
   return r.data.tenants ?? [];
}
